import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, imgUrl, IMAGES } from "../lib/api";
import { ArrowRight, Play, Mail, Youtube, Instagram, Music, Music2, Facebook, Film, Search, Mic } from "lucide-react";
import FamRadioPlayer from "../components/FamRadioPlayer";
import { useMediaPlayer } from "../lib/mediaPlayer";
import Seo from "../components/Seo";

const DiscordGlyph = ({ className }) => (
  <svg viewBox="0 0 24 24" className={`fill-current ${className || ""}`} aria-hidden="true">
    <path d="M20.317 4.369A19.79 19.79 0 0 0 16.558 3c-.2.358-.43.84-.59 1.226a18.27 18.27 0 0 0-5.487 0A12.6 12.6 0 0 0 9.882 3a19.74 19.74 0 0 0-3.76 1.369C2.692 9.07 1.79 13.65 2.24 18.166a19.93 19.93 0 0 0 6.07 3.07c.49-.67.93-1.39 1.3-2.15a12.86 12.86 0 0 1-2.05-.99c.17-.13.34-.26.5-.4a14.2 14.2 0 0 0 11.88 0c.17.14.33.27.5.4-.66.39-1.34.72-2.05.99.37.76.81 1.48 1.3 2.15a19.9 19.9 0 0 0 6.07-3.07c.53-5.25-.9-9.79-3.94-13.797ZM8.02 15.331c-1.18 0-2.157-1.08-2.157-2.41 0-1.33.96-2.41 2.157-2.41 1.2 0 2.17 1.09 2.157 2.41 0 1.33-.96 2.41-2.157 2.41Zm7.96 0c-1.18 0-2.157-1.08-2.157-2.41 0-1.33.96-2.41 2.157-2.41 1.2 0 2.17 1.09 2.157 2.41 0 1.33-.95 2.41-2.157 2.41Z" />
  </svg>
);

const HERO_SOCIALS = [
  { Icon: Instagram, url: "https://www.instagram.com/syndafam", label: "Instagram", hoverColor: "#E63946" },
  { Icon: Music, url: "https://www.tiktok.com/@syndafam", label: "TikTok", hoverColor: "#FCA311" },
  { Icon: Youtube, url: "https://youtube.com/@syndafam", label: "YouTube", hoverColor: "#E63946" },
  { Icon: Facebook, url: "https://www.facebook.com/syndafam", label: "Facebook", hoverColor: "#1877F2" },
  { Icon: DiscordGlyph, url: "https://discord.com/invite/zbaAxtqCv", label: "Discord", hoverColor: "#5865F2" },
];

export default function Home() {
  const [featureVideos, setFeatureVideos] = useState([]);
  const [artists, setArtists] = useState([]);
  const [artistQuery, setArtistQuery] = useState("");
  const [config, setConfig] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const mediaPlayer = useMediaPlayer();

  // Track deep-link (`?track=<id>`) — when present, swap the page's <Seo> to
  // per-track metadata so social sites unfurl with the track's own OG card.
  const deepLinkedTrack = useMemo(() => {
    const id = searchParams.get("track");
    if (!id) return null;
    return (mediaPlayer.tracks || []).find((t) => t.id === id) || null;
  }, [searchParams, mediaPlayer.tracks]);

  useEffect(() => {
    api.get("/feature-videos").then((r) => setFeatureVideos(r.data || [])).catch(() => {});
    api.get("/artists").then((r) => setArtists(r.data || [])).catch(() => {});
    api.get("/home/config").then((r) => setConfig(r.data || {})).catch(() => setConfig({}));
  }, []);

  const artistMatches = useMemo(() => {
    const q = artistQuery.trim().toLowerCase();
    if (!q) return [];
    return artists.filter((a) =>
      (a.name || "").toLowerCase().includes(q) ||
      (a.role || "").toLowerCase().includes(q) ||
      (a.bio || "").toLowerCase().includes(q)
    ).slice(0, 6);
  }, [artistQuery, artists]);

  const trackMatches = useMemo(() => {
    const q = artistQuery.trim().toLowerCase();
    if (!q) return [];
    return (mediaPlayer.tracks || []).filter((t) =>
      (t.title || "").toLowerCase().includes(q) ||
      (t.artist || "").toLowerCase().includes(q)
    ).slice(0, 8);
  }, [artistQuery, mediaPlayer.tracks]);

  const videoMatches = useMemo(() => {
    const q = artistQuery.trim().toLowerCase();
    if (!q) return [];
    return featureVideos.filter((v) =>
      (v.title || "").toLowerCase().includes(q) ||
      (v.description || "").toLowerCase().includes(q)
    ).slice(0, 6);
  }, [artistQuery, featureVideos]);

  // Helper to pick config value with a fallback
  const c = (k, fb = "") => (config && config[k] ? config[k] : fb);
  const marqueeItems = (config?.marquee_items && config.marquee_items.length > 0)
    ? config.marquee_items
    : ["NEW DROP ☆ MIDNIGHT CONCRETE LP", "STREET. SOUND. SOUL.", "SYNDAFAM FOR LIFE", "TUNE IN ☆ FAM RADIO"];

  return (
    <div className="bg-[#0A0A0A] text-white" data-testid="home-page">
      {deepLinkedTrack ? (
        <Seo
          title={`${deepLinkedTrack.title || "Untitled"} — ${deepLinkedTrack.artist || "SyndaFam"} · SyndaFam`.slice(0, 60)}
          description={`Listen to "${deepLinkedTrack.title || "Untitled"}"${deepLinkedTrack.artist ? ` by ${deepLinkedTrack.artist}` : ""} on SyndaFam — the independent Toronto hip-hop collective. Tap play and pull up.`.slice(0, 158)}
          path={`/?track=${deepLinkedTrack.id}`}
          image={`https://syndafam.com/api/og/track/${deepLinkedTrack.id}.jpg`}
          imageAlt={`${deepLinkedTrack.title || "Untitled"} — SyndaFam`}
          type="music.song"
          jsonLd={{
            "@context": "https://schema.org",
            "@type": "MusicRecording",
            name: deepLinkedTrack.title || "Untitled",
            url: `https://syndafam.com/?track=${deepLinkedTrack.id}`,
            image: `https://syndafam.com/api/og/track/${deepLinkedTrack.id}.jpg`,
            byArtist: {
              "@type": "MusicGroup",
              name: deepLinkedTrack.artist || "SyndaFam",
              url: "https://syndafam.com/",
            },
            inAlbum: { "@type": "MusicAlbum", name: "SyndaFam Fam Radio" },
            recordingOf: { "@type": "MusicComposition", name: deepLinkedTrack.title || "Untitled" },
          }}
        />
      ) : (
        <Seo
          title="SyndaFam | Independent Toronto Hip-Hop Collective (Canada)"
          description="SyndaFam is an independent Canadian multimedia collective — Toronto hip-hop, underground rap, cinematic music videos, digital art and original entertainment from emerging Canadian artists."
          path="/"
          jsonLd={{
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "SyndaFam — Independent Toronto Hip-Hop Collective",
            "url": "https://syndafam.com/",
            "description": "Home of SyndaFam — an independent Canadian music collective from Toronto. Fresh hip-hop music, music videos, artist roster, artwork and creative culture.",
            "isPartOf": { "@id": "https://syndafam.com/#website" },
            "about": { "@id": "https://syndafam.com/#organization" }
          }}
        />
      )}

      {/* HERO — living aurora backdrop + drifting orbs + premium reveal */}
      <section className="relative overflow-hidden border-b border-white/10 grain-overlay bg-aurora" data-testid="hero-section">
        {/* Background image — dimmed so aurora + orbs bloom through */}
        <div
          className="absolute inset-0 opacity-90"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(10,10,10,0.60) 0%, rgba(10,10,10,0.78) 55%, #0A0A0A 100%), url(${imgUrl(c("hero_image_path", IMAGES.hero))})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        {/* Living orbs — slow drift, colour-shifting via aurora bg beneath */}
        <div className="orb orb-red orb-live" style={{ width: 560, height: 560, top: "-100px", left: "-160px", animationDelay: "0s" }} />
        <div className="orb orb-amber orb-live" style={{ width: 460, height: 460, bottom: "-140px", right: "-100px", animationDelay: "-6s" }} />
        <div className="orb orb-cyan orb-live" style={{ width: 320, height: 320, top: "40%", right: "35%", opacity: 0.18, animationDelay: "-3s" }} />

        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-10 pt-16 pb-24 min-h-[85vh] flex flex-col justify-end" style={{ perspective: "1400px" }}>
          <div className="font-mono text-[10px] tracking-[0.4em] text-[#E63946] mb-4 text-glow-red reveal reveal-1">{c("hero_overline", "// VOL.001 — STREETS, SOUND & SOUL")}</div>
          <h1 className="font-display text-[12vw] md:text-[10vw] lg:text-[9vw] leading-[0.85] tracking-tighter max-w-5xl reveal reveal-2">
            {c("hero_line_1", "WE DON'T")}<br/>
            <span className="text-[#E63946] text-glow-red">{c("hero_line_2", "FOLLOW")}</span><br/>
            {c("hero_line_3", "THE WAVE.")}<br/>
            <span className="font-graffiti normal-case text-[60%] text-[#FCA311] text-glow-amber inline-block mt-2">{c("hero_graffiti", "we are the wave.")}</span>
          </h1>

          {/* Glass description card with AI-vibe conic border */}
          <div className="mt-8 max-w-xl ai-border glass-card-strong rounded-3xl p-6 sm:p-7 reveal reveal-3 float-card" data-testid="hero-description-card">
            <p className="text-white/85 text-base sm:text-lg leading-relaxed">
              {c("hero_subtitle", "SyndaFam is a music collective from the bottom of the map. We make sounds the way the streets taught us — heavy, honest, and unfiltered. Spin the records. Pull up.")}
            </p>
          </div>

          <div className="mt-10 flex items-center gap-4 sm:gap-5 flex-wrap reveal reveal-4" data-testid="hero-socials">
            {HERO_SOCIALS.map(({ Icon, url, label, hoverColor }) => (
              <a
                key={label}
                href={url}
                target="_blank"
                rel="noreferrer"
                aria-label={label}
                title={label}
                style={{ "--hover": hoverColor }}
                className="group relative w-14 h-14 sm:w-16 sm:h-16 rounded-full glass-card flex items-center justify-center transition-all duration-500 hover:scale-110 hover:border-[var(--hover)] hover:shadow-[0_0_36px_var(--hover),0_0_16px_var(--hover)]"
                data-testid={`hero-social-${label.toLowerCase()}`}
              >
                {/* breathing halo */}
                <span
                  className="absolute inset-0 rounded-full breathe"
                  style={{ background: `radial-gradient(circle, ${hoverColor}55 0%, transparent 70%)`, opacity: 0 }}
                  aria-hidden="true"
                />
                <Icon className="relative w-6 h-6 sm:w-7 sm:h-7 text-white transition-colors group-hover:text-[var(--hover)]" />
                <span className="absolute -bottom-7 left-1/2 -translate-x-1/2 font-mono text-[10px] tracking-widest text-white/0 group-hover:text-white whitespace-nowrap transition-colors">
                  {label.toUpperCase()}
                </span>
              </a>
            ))}
          </div>
        </div>

        {/* marquee */}
        <div className="relative border-t border-b border-white/10 bg-black/60 backdrop-blur-xl py-4 overflow-hidden">
          <div className="scroll-marquee">
            <div className="scroll-marquee-inner font-display text-3xl tracking-tight whitespace-nowrap">
              {Array.from({ length: 2 }).map((_, idx) => (
                <span key={idx} className="flex items-center gap-12">
                  {marqueeItems.map((item, i) => (
                    <span key={`${idx}-${i}`} className="flex items-center gap-12">
                      <span>{item}</span>
                      <span className={i % 2 === 0 ? "text-[#E63946]" : "text-[#FCA311]"}>●</span>
                    </span>
                  ))}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* AI-Overview / SEO intro band — semantically-rich descriptive paragraph */}
      <section className="relative py-14 sm:py-16 border-t border-white/5" data-testid="seo-intro-section" aria-label="About SyndaFam">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-4">
              <div className="font-mono text-[10px] tracking-[0.4em] text-[#FCA311] mb-3">// WHO WE ARE</div>
              <h2 className="font-display text-4xl sm:text-5xl leading-[0.95]">
                SYNDAFAM.<br /><span className="text-[#E63946] text-glow-red">A CANADIAN COLLECTIVE.</span>
              </h2>
            </div>
            <div className="lg:col-span-8 space-y-4 text-white/75 text-base sm:text-lg leading-relaxed">
              <p>
                <strong className="text-white">SyndaFam is an independent Canadian multimedia collective</strong> creating hip-hop music, cinematic music videos, digital art and original entertainment. Based in Toronto, Ontario, SyndaFam showcases emerging Canadian artists through music releases, videos, visual artwork and immersive digital experiences.
              </p>
              <p>
                As a Toronto hip-hop collective and independent creative studio, SyndaFam covers underground rap, cover art, streetwear-inspired visuals and live streams — a single home for the Canadian creators pushing the sound and the culture forward.
              </p>
              <p className="font-mono text-xs tracking-widest text-white/45 pt-2">
                TORONTO · ONTARIO · CANADA
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ARTIST SEARCH */}
      {artists.length > 0 && (
        <section className="relative border-t border-white/10 py-16 bg-[#0A0A0A]" data-testid="home-artist-search">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-10">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
              <div>
                <div className="font-mono text-[10px] tracking-[0.4em] text-[#E63946] mb-2">// THE ROSTER</div>
                <h2 className="font-display text-4xl sm:text-5xl flex items-center gap-3">
                  <Mic className="w-8 h-8 text-[#FCA311]" />FIND AN ARTIST
                </h2>
              </div>
              <Link
                to="/artists"
                className="font-mono text-xs tracking-widest text-white/60 hover:text-[#E63946] inline-flex items-center gap-2"
                data-testid="home-view-all-artists"
              >
                VIEW ALL <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="relative max-w-2xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <input
                type="text"
                value={artistQuery}
                onChange={(e) => setArtistQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && artistMatches.length === 1) {
                    navigate(`/artists?open=${artistMatches[0].id}`);
                  } else if (e.key === "Enter") {
                    navigate(`/artists`);
                  }
                }}
                placeholder="SEARCH ARTIST NAME, ROLE, OR VIBE…"
                className="w-full glass-card rounded-2xl border-transparent focus:border-[#E63946] outline-none pl-12 pr-4 py-4 font-mono text-sm tracking-wider placeholder:text-white/30 transition-colors"
                data-testid="home-artist-search-input"
              />
            </div>

            {artistMatches.length > 0 && (
              <div className="mt-5">
                <div className="font-mono text-[10px] tracking-[0.3em] text-white/50 mb-2">// ARTISTS — {artistMatches.length}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="home-artist-matches">
                  {artistMatches.map((a) => (
                    <Link
                      key={a.id}
                      to={`/artists?open=${a.id}`}
                      className="flex items-center gap-3 glass-card glass-hover rounded-2xl p-3 group"
                      data-testid={`home-artist-match-${a.id}`}
                    >
                      <div className="w-14 h-14 rounded-xl bg-black/60 border border-white/10 flex-shrink-0 overflow-hidden">
                        {a.photo_path ? (
                          <img src={imgUrl(a.photo_path)} alt={a.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center font-display text-2xl text-white/30">
                            {a.name.slice(0, 1).toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-[10px] tracking-widest text-[#FCA311]">{a.role || "ARTIST"}</div>
                        <div className="font-display text-lg truncate group-hover:text-[#FCA311] transition-colors">{a.name}</div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-[#FCA311]" />
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {trackMatches.length > 0 && (
              <div className="mt-5" data-testid="home-track-matches">
                <div className="font-mono text-[10px] tracking-[0.3em] text-white/50 mb-2">// TRACKS — {trackMatches.length}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {trackMatches.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => {
                        // Ensure the player is showing the right media type
                        mediaPlayer.setMediaType(t.media_type || "audio");
                        mediaPlayer.playTrack(t.id);
                        setArtistQuery("");
                        setTimeout(() => {
                          const el = document.querySelector("[data-testid='playlist-section']");
                          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                        }, 50);
                      }}
                      className="flex items-center gap-3 glass-card glass-hover rounded-2xl p-3 group text-left"
                      data-testid={`home-track-match-${t.id}`}
                    >
                      <div className="w-12 h-12 rounded-xl bg-black/60 border border-white/10 flex-shrink-0 flex items-center justify-center overflow-hidden">
                        {t.youtube_id ? (
                          <img src={`https://img.youtube.com/vi/${t.youtube_id}/mqdefault.jpg`} alt="" className="w-full h-full object-cover" loading="lazy" />
                        ) : (
                          <Music2 className="w-5 h-5 text-[#E63946]" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display text-base truncate group-hover:text-[#E63946] transition-colors">{t.title}</div>
                        <div className="font-mono text-[10px] tracking-widest text-white/50 truncate">{t.artist} · {(t.media_type || "audio").toUpperCase()}</div>
                      </div>
                      <Play className="w-4 h-4 text-white/30 group-hover:text-[#E63946]" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {videoMatches.length > 0 && (
              <div className="mt-5" data-testid="home-video-matches">
                <div className="font-mono text-[10px] tracking-[0.3em] text-white/50 mb-2">// VIDEOS — {videoMatches.length}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {videoMatches.map((v) => (
                    <div
                      key={v.id}
                      className="flex items-center gap-3 glass-card glass-hover rounded-2xl p-3"
                      data-testid={`home-video-match-${v.id}`}
                    >
                      <div className="w-12 h-12 rounded-xl bg-black/60 border border-white/10 flex-shrink-0 flex items-center justify-center">
                        <Film className="w-5 h-5 text-[#FCA311]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display text-base truncate">{v.title || "(untitled)"}</div>
                        <div className="font-mono text-[10px] tracking-widest text-white/50 truncate">{v.source_type.toUpperCase()}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {artistQuery && artistMatches.length === 0 && trackMatches.length === 0 && videoMatches.length === 0 && (
              <div className="mt-5 font-mono text-xs text-white/40" data-testid="home-search-no-results">
                No artists, tracks, or videos matching &quot;{artistQuery}&quot;.
              </div>
            )}
          </div>
        </section>
      )}

      {/* PLAYLIST */}
      <section className="relative border-t border-white/10 py-20 bg-gradient-to-b from-[#0A0A0A] via-[#100808] to-[#0A0A0A]" data-testid="playlist-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="font-mono text-[10px] tracking-[0.4em] text-[#FCA311] mb-2">// THE PLAYLIST</div>
              <h2 className="font-display text-5xl sm:text-6xl">THE FAM RADIO</h2>
            </div>
            <div className="font-mono text-[10px] tracking-widest text-white/40">
              CONTINUOUS PLAYBACK · MULTI-FORMAT
            </div>
          </div>
          <FamRadioPlayer />
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-10 py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start" data-testid="manifesto-section">
        <div className="lg:col-span-5 lg:sticky lg:top-24">
          <div className="font-mono text-[10px] tracking-[0.4em] text-white/40 mb-3">{c("manifesto_overline", "// 02 — THE MANIFESTO")}</div>
          <h2 className="font-display text-5xl sm:text-6xl leading-[0.9]">
            {c("manifesto_heading_line_1", "A FAMILY,")}<br/>{c("manifesto_heading_line_2", "NOT A LABEL.")}
          </h2>
        </div>
        <div className="lg:col-span-7 space-y-6 text-white/70 text-lg leading-relaxed">
          <p>{c("manifesto_body_1", "SyndaFam started on a stoop with a beat-up laptop and one mic.")}</p>
          <p>
            {c("manifesto_body_2", "Every record is mixed in the same basement. Every drop is numbered, dated, and signed.")} <span className="font-graffiti text-[#FCA311] text-2xl">{c("manifesto_graffiti", "no shortcuts.")}</span>
          </p>
          <div className="grid grid-cols-3 gap-3 sm:gap-4 pt-6" data-testid="manifesto-stats">
            <div className="glass-card rounded-2xl p-4 text-center glass-hover">
              <div className="font-display text-4xl text-glow-red">{c("stat_1_value", "12")}</div>
              <div className="font-mono text-[10px] tracking-widest text-white/50 mt-1">{c("stat_1_label", "YRS DEEP")}</div>
            </div>
            <div className="glass-card rounded-2xl p-4 text-center glass-hover">
              <div className="font-display text-4xl text-glow-amber">{c("stat_2_value", "38")}</div>
              <div className="font-mono text-[10px] tracking-widest text-white/50 mt-1">{c("stat_2_label", "RELEASES")}</div>
            </div>
            <div className="glass-card rounded-2xl p-4 text-center glass-hover">
              <div className="font-display text-4xl">{c("stat_3_value", "∞")}</div>
              <div className="font-mono text-[10px] tracking-widest text-white/50 mt-1">{c("stat_3_label", "FAM")}</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="relative border-t border-white/10 py-20 bg-[#E63946] grain-overlay" data-testid="cta-strip">
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-10 flex flex-col sm:flex-row items-center justify-between gap-8">
          <div>
            <div className="font-mono text-[10px] tracking-[0.4em] text-black/70 mb-2">{c("cta_overline", "// PULL UP")}</div>
            <h3 className="font-display text-4xl sm:text-6xl leading-none text-black">{c("cta_title", "GET ON THE LIST.")}</h3>
            <p className="text-black/70 mt-3 max-w-md">{c("cta_subtitle", "New tracks, new shows, no warnings. Stay close to the Fam.")}</p>
          </div>
          <Link
            to="/connect"
            className="font-display text-xl px-8 py-5 bg-black text-white hover:bg-[#0A0A0A] inline-flex items-center gap-3 transition-colors hard-shadow-yellow"
            data-testid="cta-connect-btn"
          >
            <Mail className="w-5 h-5" /> {c("cta_button_text", "CONNECT WITH US")}
          </Link>
        </div>
      </section>
    </div>
  );
}
